import React, { useEffect, useState } from 'react';
import LoadError from '../../components/LoadError';
import { formatDate } from '../../lib/utils';
import { apiGet, apiPost } from '../../contexts/AuthContext';
import { Landmark, Plus, X, User, Phone, Globe, Users as UsersIcon } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const STATUS_COLORS = {
  Pending: 'bg-ember/15 text-warning',
  'Under Review': 'bg-brand/15 text-brand-soft',
  Approved: 'bg-brand/15 text-success',
  Rejected: 'bg-ember/15 text-danger',
};

export default function EducationLoan() {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    candidate_name: '',
    fathers_name: '',
    candidate_phone: '',
    father_phone: '',
    country_applying_for: '',
  });

  const fetchLoans = () => {
    setLoadError(false);
    apiGet('/partner/loan-applications')
      .then(r => setLoans(r.data))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchLoans(); }, []);

  // Candidates (for auto-filling student details) and countries (picked manually).
  // Only candidates with a university application the admin has set to In Progress are offered.
  const [candidates, setCandidates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [candidateId, setCandidateId] = useState('');
  const [candidatesState, setCandidatesState] = useState('loading'); // loading | ready | error
  useEffect(() => {
    if (!dialogOpen) return;
    setCandidatesState('loading');
    Promise.all([apiGet('/candidates'), apiGet('/applications', { params: { status: 'In Progress' } })])
      .then(([c, a]) => {
        const inProgress = new Set((a.data || []).map(app => app.candidate_id));
        setCandidates((c.data || []).filter(x => inProgress.has(x.id)));
        setCandidatesState('ready');
      })
      .catch(() => setCandidatesState('error'));
    apiGet('/universities/countries').then(r => setCountries(r.data || [])).catch(() => {});
  }, [dialogOpen]);

  // Fills the student's own details; father's details and country stay manual.
  const pickCandidate = (cid) => {
    setCandidateId(cid);
    const c = candidates.find(x => x.id === cid);
    if (c) setForm(prev => ({ ...prev, candidate_name: c.name || prev.candidate_name, candidate_phone: c.phone || prev.candidate_phone }));
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.candidate_name.trim() || !form.fathers_name.trim() || !form.candidate_phone.trim() || !form.country_applying_for.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await apiPost('/partner/loan-applications', form);
      toast.success('Loan application created successfully');
      setDialogOpen(false);
      setForm({ candidate_name: '', fathers_name: '', candidate_phone: '', father_phone: '', country_applying_for: '' });
      setCandidateId('');
      fetchLoans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-ember tracking-tight" style={{ fontFamily: 'Outfit' }}>Education Loan</h1>
          <p className="text-ink-2 mt-1">Create and track loan applications for candidates</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-brand hover:bg-brand/90 text-white gap-2"
        >
          <Plus className="w-4 h-4" /> New Application
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {['Pending', 'Under Review', 'Approved', 'Rejected'].map(status => (
          <div key={status} className="bg-surface rounded-xl border border-contrast/[0.07] shadow-card p-4">
            <p className="text-xs text-ink-3 font-medium uppercase tracking-wider">{status}</p>
            <p className="text-2xl font-semibold text-ink mt-1">{loans.filter(l => l.status === status).length}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      {loadError ? (
        <LoadError what="your loan applications" onRetry={() => { setLoading(true); fetchLoans(); }} />
      ) : loans.length > 0 ? (
        <div className="bg-surface rounded-xl border border-contrast/[0.07] shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-contrast/5 border-b border-contrast/10">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wider">Candidate</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wider">Father's Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wider">Phone</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wider">Father Phone</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wider">Country</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ink-2 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody>
                {loans.map((loan) => (
                  <tr key={loan.id} className="border-b border-contrast/[0.07] hover:bg-contrast/5 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-surface-3 rounded-full flex items-center justify-center text-brand text-xs font-semibold">
                          {loan.candidate_name?.charAt(0)?.toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-ink">{loan.candidate_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-ink-2">{loan.fathers_name}</td>
                    <td className="px-4 py-3 text-sm text-ink-2 whitespace-nowrap">{loan.candidate_phone}</td>
                    <td className="px-4 py-3 text-sm text-ink-2 whitespace-nowrap">{loan.father_phone || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Globe className="w-3.5 h-3.5 text-ink-3" />
                        <span className="text-sm text-ink-2">{loan.country_applying_for}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`${STATUS_COLORS[loan.status] || 'bg-contrast/5 text-ink-2'} text-xs font-medium`}>
                        {loan.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-ink-3">{formatDate(loan.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-contrast/[0.07] shadow-card flex flex-col items-center justify-center py-16">
          <Landmark className="w-12 h-12 text-ink-3 mb-3" />
          <p className="text-ink-3 font-medium">No loan applications yet</p>
          <p className="text-sm text-ink-3 mt-1">Click "New Application" to create one</p>
        </div>
      )}

      {/* Create Loan Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-brand" />
              New Loan Application
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label htmlFor="loan-loan-candidate-select">Select Candidate</Label>
              <Select value={candidateId} onValueChange={pickCandidate}>
                <SelectTrigger id="loan-loan-candidate-select" className="h-10" data-testid="loan-candidate-select"><SelectValue placeholder="Choose a registered candidate to auto-fill" /></SelectTrigger>
                <SelectContent>
                  {candidates.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}{c.xpert_id ? ` (${c.xpert_id})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-ink-3" data-testid="loan-candidate-help">
                {candidatesState === 'loading' ? 'Loading candidates…'
                  : candidatesState === 'error' ? "Couldn't load candidates. Close this dialog and try again."
                  : candidates.length
                  ? "Candidates whose application is In Progress. Fills in the candidate's name and phone; father's details and country are entered manually."
                  : 'No candidates have an application In Progress yet.'}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="candidate_name">Candidate Name *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-3" />
                <Input id="candidate_name" name="candidate_name" value={form.candidate_name} onChange={handleChange} placeholder="Enter candidate name" required className="pl-9 h-10" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fathers_name">Father's Name *</Label>
              <div className="relative">
                <UsersIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-3" />
                <Input id="fathers_name" name="fathers_name" value={form.fathers_name} onChange={handleChange} placeholder="Enter father's name" required className="pl-9 h-10" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="candidate_phone">Candidate Phone *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-3" />
                  <Input id="candidate_phone" name="candidate_phone" value={form.candidate_phone} onChange={handleChange} placeholder="+1234567890" required className="pl-9 h-10" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="father_phone">Father's Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-3" />
                  <Input id="father_phone" name="father_phone" value={form.father_phone} onChange={handleChange} placeholder="+1234567890" className="pl-9 h-10" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="country_applying_for">Country Applying For *</Label>
              <Select value={form.country_applying_for} onValueChange={(v) => setForm(prev => ({ ...prev, country_applying_for: v }))}>
                <SelectTrigger id="country_applying_for" className="h-10" data-testid="loan-country-select">
                  <span className="flex items-center gap-2"><Globe className="w-4 h-4 text-ink-3" /><SelectValue placeholder="Select country" /></span>
                </SelectTrigger>
                <SelectContent>
                  {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-brand hover:bg-brand/90 text-white">
                {submitting ? 'Submitting...' : 'Submit Application'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
